import clsx from 'clsx';
import pluralize from 'pluralize';
import React, { Dispatch, SetStateAction } from 'react';
import toast from 'react-hot-toast';

import Button from '@components/Button';
import Separator from '@components/Comments/Separator';
import IconLike from '@components/Images/Rebranding/IconLike';
import IconLink from '@components/Images/Rebranding/IconLink';
import IconReply from '@components/Images/Rebranding/IconReply';
import IconReport from '@components/Images/Rebranding/IconReport';
import TimeAgo from '@components/TimeAgo';
import useCommentActions from '@hooks/useCommentActions';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { edit_community_comment_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Comment as CommentType, CommentableType } from '@utils/backend-api/comments';

interface CommentActionsProps {
  comment: CommentType;
  commentableType: CommentableType;
  onEditComment: () => void;
  setReplyingTo: Dispatch<SetStateAction<string>>;
}

export default function CommentActions({
  comment,
  commentableType,
  onEditComment,
  setReplyingTo,
}: CommentActionsProps) {
  const { currentUser } = useCurrentUser();
  const { report, toggleLike, liked, likesCount, areLikesLoading } = useCommentActions(comment);
  const { trackEvent } = useEventTracking();

  const handleLike = () => {
    toggleLike();
    trackEvent(click({ clickTarget: liked ? ClickTarget.CommentUnlike : ClickTarget.CommentLike }));
  };

  const handleLinkCopy = () => {
    const { origin, pathname } = window.location;

    navigator.clipboard.writeText(`${origin}${pathname}?commentId=${comment.id}`);
    trackEvent(click({ clickTarget: ClickTarget.CopyShareLink }));
    toast.success('Copied link to conversation');
  };

  const handleReply = () => {
    setReplyingTo(comment.user.username);
  };

  const handleReport = () => {
    // Don't track the event if the report was not confirmed
    report() && trackEvent(click({ clickTarget: ClickTarget.CommentReport }));
  };

  return (
    <div className="ml-0 mt-32 flex items-start border-0 border-b border-solid border-grayscale-gray pb-8 sm:ml-72 sm:items-center">
      <div className="flex flex-col items-start gap-24 sm:flex-row sm:items-center sm:gap-0">
        <div className="flex items-center gap-24">
          {currentUser?.permitted_to_comment && (
            <>
              <Button
                as="button"
                onClick={handleReply}
                className="comment-action --reply unstyled flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta"
              >
                <IconReply className="mr-8 w-[18px]" />
                reply
              </Button>
              <Button
                as="button"
                onClick={handleLike}
                className="comment-action --like unstyled group flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta"
                disabled={areLikesLoading}
              >
                <IconLike
                  className={clsx('mr-8 w-16', {
                    ['text-primary-magenta']: liked,
                  })}
                />
                <>
                  {likesCount > 0 && <span className="mr-4">{likesCount}</span>}
                  <span
                    className={clsx({
                      'inline-block': liked,
                    })}
                  >
                    {pluralize('like', likesCount === 0 ? 1 : likesCount)}
                  </span>
                </>
              </Button>
            </>
          )}
          <Button
            as="button"
            className="unstyled flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta"
            onClick={handleLinkCopy}
          >
            <IconLink className="text-currentColor mr-4 h-[18px] w-[18px] stroke-current" />{' '}
            <span>link</span>
          </Button>
        </div>
        <div className="flex items-center">
          <Separator className="mx-16 hidden sm:inline-block lg:mx-24" />
          {currentUser?.administrator?.is_active && !comment.promoted && (
            <>
              <Button
                as="link"
                href={edit_community_comment_path(comment.id)}
                className="comment-action --promote unstyled font-suisse-intl text-button-sm text-grayscale-gray hover:text-primary-magenta"
              >
                promote/hide
              </Button>
              <Separator className="mx-16 lg:mx-24" />
            </>
          )}
          <TimeAgo
            className="font-suisse-intl-mono text-overline-sm text-grayscale-gray"
            date={comment.created_at}
          />
        </div>
      </div>
      <div className="ml-auto flex flex-row items-center">
        {currentUser?.id === comment.user.id && commentableType === CommentableType.ForumPost && (
          <Button
            as="button"
            onClick={onEditComment}
            className="unstyled mr-16 flex items-center font-suisse-intl-mono text-byline-sm text-grayscale-gray hover:text-primary-magenta"
          >
            edit
          </Button>
        )}
        <Button
          as="button"
          onClick={handleReport}
          className="unstyled flex items-center font-suisse-screen text-byline-sm text-grayscale-gray hover:text-primary-magenta"
        >
          <IconReport className="mr-8 h-16" />
          report
        </Button>
      </div>
    </div>
  );
}
